import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PaymentIntentDetailDialog,
  type PaymentIntentDetail,
} from '@/components/payments/payment-intent-detail-dialog';
import {
  amountsValueFromLink,
  amountsValueEqualsStored,
  buildUpdateAmountsPayload,
  formatSuggestedAmounts,
  GivingLinkAmountsField,
  isAmountsValueValid,
  amountsMatchDefault,
  type GivingLinkAmountsValue,
} from '@/components/payments/giving-link-amounts-field';
import { toGivingLinkFundOptions } from '@/components/payments/giving-link-fund-options';
import { GivingLinkImageField } from '@/components/payments/giving-link-image-field';
import { HubtelAccountField } from '@/components/payments/hubtel-account-field';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { fundTypeLabel } from '@/components/shared/fund-type-select';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { MultiSelectListbox } from '@/components/ui/multi-select-listbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { PERMISSIONS, hasPermission, useCurrentMembership } from '@/lib/permissions';
import { buildGivingLinkUrl } from '@/lib/public-origins';
import { useChurchStore } from '@/stores/church-store';
import { formatDateTime, formatNumber } from '@/utils/format';

interface Fund {
  id: string;
  name: string;
  fundType: string;
  customTypeName?: string | null;
  defaultCurrency: string;
  isActive: boolean;
}

interface GivingLinkDetail {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  imageKey: string | null;
  imageUrl: string | null;
  allowedFundIds: string[];
  defaultFundId: string;
  defaultFundName?: string | null;
  suggestedAmountsMinor: number[];
  allowCustomAmount: boolean;
  currency: string;
  financialPositionItemId: string | null;
  isActive: boolean;
  viewCount: number;
  giftCount: number;
  createdAt: string;
  updatedAt: string;
}

type IntentStatus =
  | 'created'
  | 'pending'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'expired'
  | 'review_required';

const statusBadgeVariant = (status: IntentStatus): BadgeVariant => {
  if (status === 'succeeded') return 'success';
  if (status === 'failed' || status === 'cancelled' || status === 'expired') return 'danger';
  if (status === 'review_required') return 'warning';
  return 'info';
};

export const GivingLinkDetailDialog = ({
  linkId,
  open,
  onOpenChange,
  canEdit,
  initialMode = 'view',
  onShare,
}: {
  linkId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEdit: boolean;
  initialMode?: 'view' | 'edit';
  onShare?: (link: { slug: string; title: string }) => void;
}) => {
  const { t } = useTranslation('payments');
  const { t: tGiving } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canRecheck = hasPermission(membership, PERMISSIONS.PAYMENTS_RECONCILE);
  const canReconcile = canRecheck;
  const canRecordGift = hasPermission(membership, PERMISSIONS.GIVING_RECORD);
  const church = useChurchStore((state) => state.church);

  const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageKey, setImageKey] = useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [fundIds, setFundIds] = useState<string[]>([]);
  const [defaultFundId, setDefaultFundId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [amounts, setAmounts] = useState<GivingLinkAmountsValue>(() => amountsValueFromLink({
    suggestedAmountsMinor: [],
    allowCustomAmount: true,
  }));
  const [selectedGift, setSelectedGift] = useState<PaymentIntentDetail | null>(null);

  useEffect(() => {
    if (open) setMode(initialMode);
  }, [initialMode, open, linkId]);

  const detail = useQuery({
    queryKey: ['payments', 'giving-links', linkId],
    queryFn: () => api.get<GivingLinkDetail>(`/payments/giving-links/${linkId}`),
    enabled: open && Boolean(linkId),
  });

  const funds = useQuery({
    queryKey: ['giving', 'funds', 'giving-link-detail'],
    queryFn: () =>
      api.get<{ data: Fund[] }>('/giving/funds', {
        query: { page: 1, pageSize: 100 },
      }),
    enabled: open,
  });

  const gifts = useQuery({
    queryKey: ['payments', 'intents', 'by-link', linkId],
    queryFn: () =>
      api.get<{ data: PaymentIntentDetail[]; page: OffsetPage }>('/payments/intents', {
        query: {
          page: 1,
          pageSize: 50,
          givingLinkId: linkId ?? undefined,
        },
      }),
    enabled: open && Boolean(linkId) && mode === 'view',
    placeholderData: keepPreviousData,
  });

  const fundNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const fund of funds.data?.data ?? []) {
      map.set(fund.id, fund.name);
    }
    return map;
  }, [funds.data?.data]);

  const fundOptions = useMemo(() => {
    const rows = funds.data?.data ?? [];
    const byId = new Map(rows.map((fund) => [fund.id, fund]));
    const selectedMissing = (detail.data?.allowedFundIds ?? []).filter((id) => !byId.has(id));
    const options = toGivingLinkFundOptions(
      rows.filter((fund) => fund.fundType !== 'tithe' || fundIds.includes(fund.id)),
      {
        description: (fund) =>
          tGiving('form.fundOptionDescription', {
            type: fundTypeLabel(fund, tGiving),
            currency: fund.defaultCurrency,
          }),
        inactiveSuffix: t('links.status.inactive'),
      },
    );
    for (const id of selectedMissing) {
      options.push({ value: id, label: fundNameById.get(id) ?? id });
    }
    return options;
  }, [detail.data?.allowedFundIds, fundIds, fundNameById, funds.data?.data, t, tGiving]);

  useEffect(() => {
    if (!detail.data) return;
    setTitle(detail.data.title);
    setDescription(detail.data.description ?? '');
    setImageKey(detail.data.imageKey);
    setImagePreviewUrl(detail.data.imageUrl);
    setFundIds(detail.data.allowedFundIds);
    setDefaultFundId(detail.data.defaultFundId);
    setAccountId(detail.data.financialPositionItemId);
    setAmounts(amountsValueFromLink(detail.data));
  }, [detail.data]);

  useEffect(() => {
    if (defaultFundId && !fundIds.includes(defaultFundId)) {
      setDefaultFundId(fundIds[0] ?? null);
    }
    if (!defaultFundId && fundIds[0]) {
      setDefaultFundId(fundIds[0]);
    }
  }, [defaultFundId, fundIds]);

  const save = useMutation({
    mutationFn: () =>
      api.patch<GivingLinkDetail>(`/payments/giving-links/${linkId}`, {
        title: title.trim(),
        description: description.trim() || null,
        imageKey,
        allowedFundIds: fundIds,
        defaultFundId,
        financialPositionItemId: accountId,
        ...buildUpdateAmountsPayload(amounts),
      }),
    onSuccess: () => {
      toast.success(t('links.detail.updated'));
      void queryClient.invalidateQueries({ queryKey: ['payments', 'giving-links'] });
      setMode('view');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const giftColumns: DataTableColumn<PaymentIntentDetail>[] = useMemo(
    () => [
      {
        id: 'date',
        header: t('attempts.columns.date'),
        cell: (row) => formatDateTime(row.completedAt ?? row.createdAt),
      },
      {
        id: 'amount',
        header: t('attempts.columns.amount'),
        money: (row) => ({ amount: row.amountMinor, currency: row.currency }),
      },
      {
        id: 'fund',
        header: t('attempts.columns.fund'),
        cell: (row) => row.fundName?.trim() || '—',
        hideBelowMd: true,
      },
      {
        id: 'status',
        header: t('attempts.columns.status'),
        cell: (row) => (
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusBadgeVariant(row.status)}>
              {t(`attempts.status.${row.status}`)}
            </Badge>
            {row.isTest ? <Badge variant="neutral">{t('attempts.testBadge')}</Badge> : null}
          </div>
        ),
      },
      {
        id: 'giver',
        header: t('attempts.columns.giver'),
        cell: (row) => row.giverName?.trim() || '—',
        hideBelowMd: true,
      },
    ],
    [t],
  );

  const link = detail.data;
  const url = link && church?.slug ? buildGivingLinkUrl(church.slug, link.slug) : null;

  const dirty =
    Boolean(link) &&
    (title.trim() !== link!.title ||
      (description.trim() || null) !== (link!.description ?? null) ||
      (imageKey ?? null) !== (link!.imageKey ?? null) ||
      defaultFundId !== link!.defaultFundId ||
      fundIds.length !== link!.allowedFundIds.length ||
      fundIds.some((id) => !link!.allowedFundIds.includes(id)) ||
      !amountsValueEqualsStored(amounts, link!));

  const canSubmit =
    canEdit &&
    mode === 'edit' &&
    title.trim().length > 0 &&
    fundIds.length > 0 &&
    Boolean(defaultFundId) &&
    isAmountsValueValid(amounts) &&
    dirty &&
    !save.isPending;

  const amountCurrency =
    (funds.data?.data ?? []).find((fund) => fund.id === defaultFundId)?.defaultCurrency ??
    link?.currency;

  const allowedFundLabels = (link?.allowedFundIds ?? [])
    .map((id) => fundNameById.get(id))
    .filter(Boolean) as string[];

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) setSelectedGift(null);
          onOpenChange(next);
        }}
      >
        <DialogContent size="xl" className="overflow-visible" closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>
              {link?.title?.trim() || t('links.detail.dialogTitle')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {detail.isLoading || !link ? (
              <div className="space-y-3" aria-busy>
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : mode === 'edit' ? (
              <div className="space-y-6">
                <div>
                  <Label htmlFor="giving-link-detail-title">{t('links.form.title')}</Label>
                  <Input
                    id="giving-link-detail-title"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label htmlFor="giving-link-detail-description">
                    {t('links.form.description')}
                  </Label>
                  <Textarea
                    id="giving-link-detail-description"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    rows={3}
                  />
                </div>
                <GivingLinkImageField
                  idPrefix="giving-link-detail"
                  imageKey={imageKey}
                  previewUrl={imagePreviewUrl}
                  onChange={({ imageKey: nextKey, previewUrl }) => {
                    setImageKey(nextKey);
                    setImagePreviewUrl(previewUrl);
                  }}
                />
                <div>
                  <Label>{t('links.form.funds')}</Label>
                  <MultiSelectListbox
                    value={fundIds}
                    onChange={setFundIds}
                    options={fundOptions}
                    placeholder={t('links.form.fundsPlaceholder')}
                    emptyMessage={t('links.form.fundsEmpty')}
                    popoverPortal="dialog"
                  />
                </div>
                <div>
                  <Label>{t('links.form.defaultFund')}</Label>
                  <Listbox
                    value={defaultFundId}
                    onChange={setDefaultFundId}
                    options={fundOptions.filter((option) => fundIds.includes(option.value))}
                    placeholder={t('links.form.defaultFundPlaceholder')}
                    popoverPortal="dialog"
                    disabled={fundIds.length === 0}
                  />
                </div>
                <HubtelAccountField
                  open={mode === 'edit'}
                  currency={amountCurrency}
                  value={accountId}
                  onChange={setAccountId}
                />
                <GivingLinkAmountsField
                  idPrefix="giving-link-detail"
                  value={amounts}
                  onChange={setAmounts}
                  currency={amountCurrency}
                />
              </div>
            ) : (
              <div className="space-y-6">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={link.isActive ? 'success' : 'neutral'}>
                      {link.isActive ? t('links.status.active') : t('links.status.inactive')}
                    </Badge>
                    <span className="text-body-sm text-fg-secondary">
                      {t('links.detail.stats', {
                        gifts: formatNumber(link.giftCount),
                        opens: formatNumber(link.viewCount),
                      })}
                    </span>
                  </div>

                  {link.description?.trim() ? (
                    <p className="text-body-sm text-fg-secondary">{link.description}</p>
                  ) : null}

                  {link.imageUrl ? (
                    <img
                      src={link.imageUrl}
                      alt=""
                      className="max-h-48 w-full rounded-lg object-cover"
                    />
                  ) : null}

                  {url ? (
                    <div>
                      <p className="text-caption text-fg-tertiary">{t('links.detail.url')}</p>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-0.5 block break-all text-body-sm text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                      >
                        {url}
                      </a>
                    </div>
                  ) : null}

                  <div>
                    <p className="text-caption text-fg-tertiary">{t('links.form.funds')}</p>
                    <p className="mt-0.5 text-body-sm text-fg">
                      {allowedFundLabels.length > 0
                        ? allowedFundLabels.join(', ')
                        : '—'}
                    </p>
                  </div>

                  <div>
                    <p className="text-caption text-fg-tertiary">
                      {amountsMatchDefault(link.suggestedAmountsMinor)
                        ? t('links.detail.amountsDefault')
                        : t('links.detail.amounts')}
                    </p>
                    <p className="mt-0.5 text-body-sm text-fg">
                      {formatSuggestedAmounts(link.suggestedAmountsMinor, link.currency)}
                    </p>
                    <p className="mt-1 text-caption text-fg-secondary">
                      {link.allowCustomAmount
                        ? t('links.detail.allowCustomAmount')
                        : t('links.detail.customAmountOff')}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-body font-medium text-fg">
                    {t('links.detail.giftsTitle')}
                  </h3>
                  <DataTable
                    rows={gifts.data?.data ?? []}
                    columns={giftColumns}
                    rowKey={(row) => row.id}
                    loading={gifts.isLoading}
                    empty={
                      <p className="text-center text-body-sm text-fg-secondary">
                        {t('links.detail.giftsEmpty')}
                      </p>
                    }
                    onRowClick={(row) => setSelectedGift(row)}
                  />
                </div>
              </div>
            )}
          </DialogBody>
          <DialogFooter className="justify-end">
            {mode === 'view' && link && onShare ? (
              <Button
                variant="secondary"
                onClick={() => {
                  onShare({ slug: link.slug, title: link.title });
                  onOpenChange(false);
                }}
              >
                {t('links.share')}
              </Button>
            ) : null}
            {mode === 'view' && canEdit ? (
              <Button variant="secondary" onClick={() => setMode('edit')}>
                {t('links.detail.edit')}
              </Button>
            ) : null}
            {mode === 'edit' ? (
              <Button
                variant="secondary"
                onClick={() => {
                  if (detail.data) {
                    setTitle(detail.data.title);
                    setDescription(detail.data.description ?? '');
                    setImageKey(detail.data.imageKey);
                    setImagePreviewUrl(detail.data.imageUrl);
                    setFundIds(detail.data.allowedFundIds);
                    setDefaultFundId(detail.data.defaultFundId);
                    setAmounts(amountsValueFromLink(detail.data));
                  }
                  setMode('view');
                }}
              >
                {tCommon('action.cancel')}
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                {tCommon('action.close')}
              </Button>
            )}
            {mode === 'edit' ? (
              <Button disabled={!canSubmit} onClick={() => save.mutate()}>
                {tCommon('action.save')}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PaymentIntentDetailDialog
        intent={selectedGift}
        open={Boolean(selectedGift)}
        canRecheck={canRecheck}
        canReconcile={canReconcile}
        canRecordGift={canRecordGift}
        onIntentUpdated={setSelectedGift}
        onRecheckSuccess={() => {
          void queryClient.invalidateQueries({ queryKey: ['payments', 'giving-links'] });
        }}
        onOpenChange={(next) => {
          if (!next) setSelectedGift(null);
        }}
      />
    </>
  );
};
